import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Badge,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Inbox,
  Send,
  Star,
  PenSquare,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchInboxStats } from "../../../../redux/actions/inboxAction";
import EmailList from "./components/EmailList";
import EmailThread from "./components/EmailThread";
import ComposeEmail from "./components/ComposeEmail";
import EmailSettings from "./components/EmailSettings";

const SECTION_KEYS = [
  { key: "inbox", labelKey: "inbox_inbox", icon: Inbox },
  { key: "sent", labelKey: "inbox_sent", icon: Send },
  { key: "starred", labelKey: "inbox_starred", icon: Star },
  { key: "compose", labelKey: "inbox_compose", icon: PenSquare },
  { key: "settings", labelKey: "inbox_settings", icon: Settings },
];

const InboxScreen = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { stats } = useSelector((state) => state.inbox);

  useEffect(() => {
    dispatch(fetchInboxStats());
  }, [dispatch]);

  // Derive active section from URL
  const pathParts = location.pathname.replace(/^\/admin\/inbox\/?/, "").split("/").filter(Boolean);
  const activeSection = SECTION_KEYS.find((s) => s.key === pathParts[0])?.key
    || (pathParts[0] === "thread" ? "thread" : "inbox");
  const threadId = pathParts[0] === "thread" ? pathParts[1] : null;

  const handleSectionChange = (key) => {
    navigate(`/admin/inbox/${key === "inbox" ? "" : key}`);
  };

  const renderContent = () => {
    if (threadId) return <EmailThread threadId={threadId} />;
    switch (activeSection) {
      case "inbox":
        return <EmailList folder="inbox" />;
      case "sent":
        return <EmailList folder="sent" />;
      case "starred":
        return <EmailList folder="starred" />;
      case "compose":
        return <ComposeEmail />;
      case "settings":
        return <EmailSettings />;
      default:
        return <EmailList folder="inbox" />;
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 400, fontSize: "1.5rem", color: theme.palette.text.primary, mb: 0.5,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
        >
          {t("inbox_title")}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {t("inbox_description")}
        </Typography>
      </Box>

      {/* Sub-Navigation */}
      <Box
        sx={{
          display: "flex", gap: 0.75, mb: 3, flexWrap: "wrap", pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {SECTION_KEYS.map(({ key, labelKey, icon: Icon }) => {
          const isActive = activeSection === key;
          const showBadge = key === "inbox" && stats.unread_count > 0;
          return (
            <Badge
              key={key}
              badgeContent={showBadge ? stats.unread_count : 0}
              color="error"
              sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", height: 16, minWidth: 16 } }}
            >
              <Chip
                icon={<Icon size={14} strokeWidth={isActive ? 2 : 1.5} />}
                label={t(labelKey)}
                onClick={() => handleSectionChange(key)}
                variant={isActive ? "filled" : "outlined"}
                size="small"
                sx={{
                  borderRadius: "8px", fontWeight: isActive ? 600 : 500, fontSize: "0.8rem", height: 32,
                  transition: "all 0.2s",
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  borderColor: isActive ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider,
                  "& .MuiChip-icon": { color: isActive ? theme.palette.primary.main : theme.palette.text.secondary },
                  "&:hover": {
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.divider, 0.5),
                  },
                }}
              />
            </Badge>
          );
        })}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>{renderContent()}</Box>
    </Box>
  );
};

export default InboxScreen;
